import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Nav,
  Tab,
} from "react-bootstrap";
import LogoUploadField from "../../../components/common/LogoUploadField";
import {
  FaSave,
  FaTimes,
  FaBuilding,
  FaGlobe,
  FaMapMarkerAlt,
  FaCheck,
} from "react-icons/fa";
import {
  createCompany,
  updateCompany,
  clearError,
} from "../../../features/companies/companiesSlice";
import {
  CompanyCreate,
  CompanyUpdate,
  Company,
} from "../../../features/companies/companiesService";
import { AppDispatch, RootState } from "../../../store";
import { Icon } from "../../../components/common/Icon";
import { useTranslation } from "react-i18next";
import { getCompanyTypeOptions } from "../../../utils/permissions";

interface CompanyFormModalProps {
  show: boolean;
  onHide: () => void;
  company?: Company;
  initialCompanyType?: string;
}

const getInitialFormData = (companyType: string = "client"): CompanyCreate => {
  return {
    company_name: "",
    company_type: companyType,
    industry: "",
    establishment_date: "",
    description: "",
    website: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: "",
    postal_code: "",
    tax_id: "",
    company_size: "",
    annual_revenue: undefined,
    logo_url: "",
    is_active: true,
  };
};

const CompanyFormModal: React.FC<CompanyFormModalProps> = ({
  show,
  onHide,
  company,
  initialCompanyType,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { isLoading, error } = useSelector(
    (state: RootState) => state.companies
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState<CompanyCreate>(
    getInitialFormData(company?.company_type || "client")
  );
  const [validated, setValidated] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [actualLogoUrl, setActualLogoUrl] = useState<string>("");

  useEffect(() => {
    if (show) {
      dispatch(clearError());
    }
  }, [show, dispatch]);

  useEffect(() => {
    if (company) {
      const logoUrl = company.logo_url || "";
      setFormData({
        company_name: company.company_name || "",
        company_type: company.company_type || "client",
        industry: company.industry || "",
        establishment_date: company.establishment_date || "",
        description: company.description || "",
        website: company.website || "",
        email: company.email || "",
        phone: company.phone || "",
        address: company.address || "",
        city: company.city || "",
        country: company.country || "",
        postal_code: company.postal_code || "",
        tax_id: company.tax_id || "",
        company_size: company.company_size || "",
        annual_revenue: company.annual_revenue,
        logo_url: logoUrl,
        is_active: company.is_active,
      });
      setActualLogoUrl(logoUrl && !logoUrl.startsWith("data:") ? logoUrl : "");
    } else {
      setFormData(getInitialFormData(initialCompanyType || "client"));
      setActualLogoUrl("");
    }
    setValidated(false);
    setActiveTab("basic");
  }, [company, show, initialCompanyType]);

  const isEditing = Boolean(company);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      const checkboxInput = e.target as HTMLInputElement;
      setFormData((prev) => ({ ...prev, [name]: checkboxInput.checked }));
    } else if (name === "annual_revenue") {
      setFormData((prev) => ({
        ...prev,
        [name]: value ? Number(value) : undefined,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const buildCleanCompanyCreate = (data: CompanyCreate): CompanyCreate => {
    const requiredKeys: (keyof CompanyCreate)[] = ["company_name", "company_type"];
    const clean: Partial<CompanyCreate> = {};
    (Object.keys(data) as (keyof CompanyCreate)[]).forEach((key) => {
      const value = data[key];
      if (requiredKeys.includes(key)) {
        clean[key] = value as never;
        return;
      }
      if (value === undefined || value === null) return;
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.length === 0) return;
        if (key === "establishment_date" && trimmed === "") return;
        if (key === "logo_url") {
          const logoValue = actualLogoUrl || trimmed;
          if (logoValue && !logoValue.startsWith("data:")) {
            clean[key] = logoValue as never;
          }
          return;
        }
        clean[key] = trimmed as never;
        return;
      }
      clean[key] = value as never;
    });
    return clean as CompanyCreate;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      if (!formData.company_name) setActiveTab("basic");
      return;
    }
    setValidated(true);
    try {
      if (isEditing && company) {
        await dispatch(
          updateCompany({
            companyId: company.id,
            companyData: buildCleanCompanyCreate(formData) as CompanyUpdate,
          })
        ).unwrap();
      } else {
        await dispatch(
          createCompany(buildCleanCompanyCreate(formData) as CompanyCreate)
        ).unwrap();
      }
      onHide();
    } catch {
      // Error shown from reducer
    }
  };

  const isTabCompleted = (tabKey: string) => {
    switch (tabKey) {
      case "basic":
        return formData.company_name.trim() !== "";
      case "contact":
        return !!(formData.email || formData.phone || formData.website);
      case "address":
        return !!(formData.address || formData.city || formData.country);
      default:
        return false;
    }
  };

  const modalTitle = isEditing ? t("edit_company") : t("add_new_company");
  const modalSubtitle = isEditing
    ? t("edit_company_details")
    : t("enter_company_name");

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      fullscreen="sm-down"
      backdrop="static"
      keyboard={false}
      className="modern-modal company-form-modal"
    >
      <Form
        noValidate
        validated={validated}
        onSubmit={handleSubmit}
        className="d-flex flex-column h-100"
      >
        <Modal.Header closeButton className="modern-modal-header">
          <Modal.Title className="d-flex align-items-center gap-3 w-100">
            <div className="header-icon-wrapper p-2 rounded-3 bg-soft-primary text-primary flex-shrink-0">
              <Icon icon={FaBuilding} size="1.25rem" />
            </div>
            <div className="flex-grow-1 min-w-0">
              <span className="d-block modal-title-text">{modalTitle}</span>
              <small className="modal-subtitle-text text-muted d-block">
                {modalSubtitle}
              </small>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="modern-modal-body">
          {error && (
            <Alert
              variant="danger"
              className="d-flex align-items-center gap-2 mb-4 modern-alert"
              dismissible
              onClose={() => dispatch(clearError())}
            >
              <strong>{t("error")}:</strong>
              <span>{error}</span>
            </Alert>
          )}

          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || "basic")}
          >
            <Nav variant="pills" className="company-form-tabs nav-tabs mb-4">
              <Nav.Item>
                <Nav.Link eventKey="basic" className="d-flex align-items-center gap-2">
                  <Icon icon={FaBuilding} size="0.9rem" />
                  <span>{t("basic_information")}</span>
                  {isTabCompleted("basic") && (
                    <Icon icon={FaCheck} className="text-success ms-1" size="0.75rem" />
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="contact" className="d-flex align-items-center gap-2">
                  <Icon icon={FaGlobe} size="0.9rem" />
                  <span>{t("contact_information")}</span>
                  {isTabCompleted("contact") && (
                    <Icon icon={FaCheck} className="text-success ms-1" size="0.75rem" />
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="address" className="d-flex align-items-center gap-2">
                  <Icon icon={FaMapMarkerAlt} size="0.9rem" />
                  <span>{t("address_information")}</span>
                  {isTabCompleted("address") && (
                    <Icon icon={FaCheck} className="text-success ms-1" size="0.75rem" />
                  )}
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="basic" className="company-form-pane">
                <div className="section-header-modern mb-3">
                  <div className="section-header-left">
                    <h6 className="section-title mb-0">{t("basic_information")}</h6>
                    <p className="section-subtitle mb-0">{t("enter_company_name")}</p>
                  </div>
                </div>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>
                        {t("company_name")} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder={t("enter_company_name")}
                        className="form-control-modern"
                      />
                      <Form.Control.Feedback type="invalid">
                        {t("please_enter_company_name")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        {t("company_type")} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        required
                        name="company_type"
                        value={formData.company_type}
                        onChange={handleChange}
                        disabled={
                          (!!initialCompanyType && !isEditing) ||
                          (!!user?.roles?.includes("agency_owner") && !isEditing)
                        }
                        className="form-control-modern"
                      >
                        {(() => {
                          const options = getCompanyTypeOptions(user);
                          const values = new Set(options.map((o) => o.value));
                          const ensure: { value: string; label: string }[] = [];
                          const currentValue = formData.company_type;
                          if (currentValue && !values.has(currentValue)) {
                            const label =
                              currentValue === "agency"
                                ? "Agency"
                                : currentValue === "single"
                                  ? "Single"
                                  : "Client";
                            ensure.push({ value: currentValue, label });
                          }
                          return [...ensure, ...options].map((option) => (
                            <option key={option.value} value={option.value}>
                              {t(option.label.toLowerCase())}
                            </option>
                          ));
                        })()}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("industry")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        placeholder={t("enter_industry")}
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("establishment_date")}</Form.Label>
                      <Form.Control
                        type="date"
                        name="establishment_date"
                        value={formData.establishment_date}
                        onChange={handleChange}
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="d-flex align-items-end h-100 pb-2">
                      <Form.Check
                        type="switch"
                        id="is_active"
                        name="is_active"
                        label={
                          formData.is_active
                            ? t("company_is_active")
                            : t("company_is_inactive")
                        }
                        checked={formData.is_active}
                        onChange={handleChange}
                        className="form-switch-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>{t("description")}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder={t("enter_company_description")}
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab.Pane>

              <Tab.Pane eventKey="contact" className="company-form-pane">
                <div className="section-header-modern mb-3">
                  <div className="section-header-left">
                    <h6 className="section-title mb-0">{t("contact_information")}</h6>
                    <p className="section-subtitle mb-0">{t("logo_url")}</p>
                  </div>
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("website")}</Form.Label>
                      <Form.Control
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        placeholder="https://example.com"
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("email")}</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="contact@company.com"
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>{t("phone")}</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+90 555 123 45 67"
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <LogoUploadField
                      companyId={company?.id}
                      value={formData.logo_url || ""}
                      onChange={(url) =>
                        setFormData((prev) => ({ ...prev, logo_url: url }))
                      }
                      onLogoUrlChange={setActualLogoUrl}
                      label={t("logo_url")}
                      helperText={t("upload_logo_or_enter_url")}
                    />
                  </Col>
                </Row>
              </Tab.Pane>

              <Tab.Pane eventKey="address" className="company-form-pane">
                <div className="section-header-modern mb-3">
                  <div className="section-header-left">
                    <h6 className="section-title mb-0">{t("address_information")}</h6>
                    <p className="section-subtitle mb-0">{t("address")}</p>
                  </div>
                </div>
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>{t("address")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder={t("enter_full_address")}
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("city")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder={t("enter_city")}
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("country")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder={t("enter_country")}
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>{t("postal_code")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleChange}
                        placeholder={t("enter_postal_code")}
                        className="form-control-modern"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Modal.Body>

        <Modal.Footer className="modern-modal-footer border-0 pt-0 pb-4 justify-content-end px-4">
          <div className="d-flex align-items-center justify-content-end gap-2">
            <Button
              variant="outline-secondary"
              className="btn-minimal"
              onClick={onHide}
              disabled={isLoading}
            >
              <Icon icon={FaTimes} className="me-2" />
              {t("cancel")}
            </Button>
            <Button
              variant="info"
              className="btn-minimal"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {t("saving")}
                </>
              ) : (
                <>
                  <Icon icon={FaSave} className="me-2" />
                  {isEditing ? t("update") : t("save")}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CompanyFormModal;
